import * as React from 'react';
import {
  PushSubscriptionHealth,
  PushNotificationStats,
  PushRecentFailures,
  PushNotificationDeliveryStats,
} from 'cb-model';
import { List, PageTitle, QueryMulti, Section } from 'gossamer-front-end';
import { Tab, Tabs } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

/**
 * Push Notification Diagnostics Display Page
 *
 * Provides comprehensive overview of push notification system health and performance:
 * - Subscription Health: Active subscriptions and their health status
 * - Notification Stats: Hourly aggregated delivery statistics
 * - Recent Failures: Last 100 failures for debugging
 * - Delivery Stats: Engagement metrics (display rate, click-through rate)
 */
export const PushNotificationDiagnostics = (): React.JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'health';

  return (
    <>
      <PageTitle
        title="Push Notification Diagnostics"
        subtitle="Monitor push notification health, performance, and delivery metrics"
      />

      <Tabs
        variant="underline"
        activeKey={activeTab}
        onSelect={(tab) => {
          const newParams = new URLSearchParams();
          if (tab) newParams.set('tab', tab);
          setSearchParams(newParams);
        }}
      >
        {/* Tab 1: Subscription Health */}
        <Tab eventKey="health" title="Subscription Health">
          <Section subtitle="View the health status of all active push subscriptions">
            <QueryMulti
              entity={PushSubscriptionHealth}
              queryOptions={{
                order: [
                  ['health_status', 'ASC'],
                  ['failure_count', 'DESC'],
                ],
              }}
              render={List}
            />
          </Section>

          <div className="mt-3 p-3 bg-light border rounded">
            <h6>Health Status Legend:</h6>
            <ul className="mb-0">
              <li>
                <strong>Healthy:</strong> No failures, subscription working normally
              </li>
              <li>
                <strong>Degraded:</strong> 1-2 consecutive failures, may recover
              </li>
              <li>
                <strong>Unhealthy:</strong> 3+ consecutive failures, likely expired or invalid
              </li>
            </ul>
          </div>
        </Tab>

        {/* Tab 2: Notification Statistics */}
        <Tab eventKey="stats" title="Notification Stats Statistics">
          <Section subtitle="Hourly aggregated statistics for the last 7 days">
            <QueryMulti
              entity={PushNotificationStats}
              queryOptions={{
                order: [['hour', 'DESC']],
              }}
              render={List}
            />
          </Section>

          <div className="mt-3 p-3 bg-light border rounded">
            <h6>Statistics Overview:</h6>
            <ul className="mb-0">
              <li>
                <strong>Status:</strong> sent (successful), failed (error), expired (410/404)
              </li>
              <li>
                <strong>Response Time:</strong> Time taken to send notification in milliseconds
              </li>
              <li>
                <strong>Type:</strong> message, test, system, reminder, alert
              </li>
            </ul>
          </div>
        </Tab>

        {/* Tab 3: Recent Failures */}
        <Tab eventKey="failures" title="Recent Failures">
          <Section subtitle="Last 100 failures in the past 24 hours for debugging">
            <QueryMulti
              entity={PushRecentFailures}
              queryOptions={{
                order: [['created_at', 'DESC']],
              }}
              render={List}
            />
          </Section>

          <div className="mt-3 p-3 bg-light border rounded">
            <h6>Common Error Codes:</h6>
            <ul className="mb-0">
              <li>
                <strong>410 Gone:</strong> Push subscription expired (user unsubscribed or browser cleared data)
              </li>
              <li>
                <strong>404 Not Found:</strong> Invalid endpoint (subscription no longer valid)
              </li>
              <li>
                <strong>401 Unauthorized:</strong> VAPID authentication failed
              </li>
              <li>
                <strong>400 Bad Request:</strong> Malformed notification payload
              </li>
            </ul>
          </div>
        </Tab>

        {/* Tab 4: Delivery & Engagement Stats */}
        <Tab eventKey="delivery" title="Delivery & Engagement">
          <Section subtitle="Hourly delivery and engagement metrics for the last 7 days">
            <QueryMulti
              entity={PushNotificationDeliveryStats}
              queryOptions={{
                order: [['hour', 'DESC']],
              }}
              render={List}
            />
          </Section>

          <div className="mt-3 p-3 bg-light border rounded">
            <h6>Metrics Explained:</h6>
            <ul className="mb-0">
              <li>
                <strong>Total Sent:</strong> Number of notifications sent via Web Push
              </li>
              <li>
                <strong>Displayed:</strong> Notifications confirmed as shown to user (client-side tracking)
              </li>
              <li>
                <strong>Clicked:</strong> Notifications that user clicked on
              </li>
              <li>
                <strong>Dismissed:</strong> Notifications that user dismissed without clicking
              </li>
              <li>
                <strong>Likely Failed:</strong> Sent &gt;5 minutes ago but no display confirmation
              </li>
              <li>
                <strong>Display Rate %:</strong> Percentage of sent notifications that were displayed
              </li>
              <li>
                <strong>Click-Through Rate %:</strong> Percentage of displayed notifications that were clicked
              </li>
            </ul>
          </div>
        </Tab>
      </Tabs>
    </>
  );
};
